import styled from '@emotion/styled';
import { Spin, Tabs } from 'antd';
import Down from 'assets/svg/Down';

type HeaderTitleProps = {
    title: string;
    withVs?: boolean;
    comparisonLabel?: string;
    sortActive?: 'asc' | 'desc' | null;
    onSortAsc?: () => void;
    onSortDesc?: () => void;
    /** Shown beside the title while this KPI column's data is still loading */
    pending?: boolean;
    sortAscTestId?: string;
    sortDescTestId?: string;
};

type MetaInputBounds = {
    min_val?: number;
    max_val?: number;
};

type MetaInputConfig = {
    input_id: string;
    input_type?: string;
    default_value?: string | number | null;
    placeholder?: string;
    increment_rate?: number;
    bounds?: MetaInputBounds;
};

const selectedRowClassName = 'performance-summary-selected-row';
const tableHeaderContentClassName = 'performance-summary-table-header-content';

const SectionCard = styled.div`
    overflow: hidden;
    border-radius: 0.5rem;
    border: 1px solid rgba(0, 0, 0, 0.06);
    background: #ffffff;
`;

const StyledSectionTabs = styled(Tabs)`
    .ant-tabs-nav {
        margin: 0;
        padding: 0 0.5rem;
    }

    .ant-tabs-content-holder {
        display: none;
    }

    .ant-tabs-tab {
        margin-right: 1px !important;
        background: rgba(0, 0, 0, 0.02) !important;
        border-color: #f0f0f0 !important;
        border-bottom: 0 !important;
        border-radius: 5px 5px 0 0 !important;
        padding: 6px 16px !important;
        font-size: 16px;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.88);
        border-color: #d9d9d9 !important;
    }

    .ant-tabs-tab-active {
        background: #ffffff !important;
    }

    .ant-tabs-tab.ant-tabs-tab-active {
        background: #ffffff !important;
    }

    .ant-tabs-tab-active .ant-tabs-tab-btn {
        color: #44338a !important;
        font-weight: 500;
    }
    
    .ant-tabs-tab-btn {
        font-size: 16px !important;
    }

    .ant-tabs-nav::before {
        border-bottom-color: #f0f0f0 !important;
    }
`;

const StyledTableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 555px;
    max-height: 555px;
    overflow: hidden;

    .ant-table-container {
        display: flex;
        flex: 1;
        min-height: 0;
        flex-direction: column;
        isolation: isolate;
    }

    .ant-table-wrapper {
        display: flex;
        flex: 1;
        min-height: 555px;
        flex-direction: column;
    }

    .ant-spin-nested-loading,
    .ant-spin-container,
    .ant-table {
        display: flex;
        flex: 1;
        min-height: 0;
        flex-direction: column;
    }

    .ant-table-content {
        flex: 1;
        min-height: 0;
        overflow: scroll !important;
    }

    .ant-table-body {
        flex: 1;
        min-height: 0;
        overflow-y: scroll !important;
    }

    .ant-table {
        border-radius: 0;
    }

    .ant-table-thead > tr > th {
        color: rgba(0, 0, 0, 0.88) !important;
        font-size: 16px !important;
        font-weight: 600 !important;
        line-height: 22px !important;
        padding: 16px !important;
        height: 54px !important;
        border-bottom: 1px solid #f0f0f0 !important;
        background: #fafafa !important;
    }

    .ant-table-thead > tr > th::before {
        display: none !important;
    }

    .${tableHeaderContentClassName} {
        min-height: 22px;
    }

    .ant-table-tbody > tr:not(.ant-table-measure-row) > td {
        padding: 16px !important;
        height: 54px !important;
        color: rgba(0, 0, 0, 0.88) !important;
        font-size: 16px !important;
        line-height: 22px !important;
    }

    .ant-table-tbody > tr.${selectedRowClassName} > td {
        background: #f7f5ff !important;
    }

    .ant-table-cell {
        // border-right: 1px solid #f0f0f0 !important;
    }

    .ant-table-thead .ant-table-cell,
    .ant-table-tbody .ant-table-cell {
        position: relative;
        z-index: 0 !important;
    }

    .ant-table-thead .ant-table-cell:not(.ant-table-cell-fix-left):not(.ant-table-cell-fix-left-last) {
        overflow: visible;
    }

    .ant-table-tbody .ant-table-cell:not(.ant-table-cell-fix-left):not(.ant-table-cell-fix-left-last) {
        overflow: hidden;
    }

    .ant-table-thead .ant-table-cell-fix-left,
    .ant-table-thead .ant-table-cell-fix-left-first,
    .ant-table-thead .ant-table-cell-fix-left-last,
    .ant-table-tbody .ant-table-cell-fix-left,
    .ant-table-tbody .ant-table-cell-fix-left-first,
    .ant-table-tbody .ant-table-cell-fix-left-last {
        position: sticky !important;
        z-index: 1 !important;
    }

    th .ant-table-cell-fix-left {
        background: #fff !important;
    }

    .ant-table-thead .ant-table-cell-fix-left-last::after,
    .ant-table-tbody .ant-table-cell-fix-left-last::after {
        position: absolute;
        top: 0;
        height: 100%;
        pointer-events: none;
        z-index: 1;
    }

    .ant-table-thead > tr > th:last-child,
    .ant-table-tbody > tr:not(.ant-table-measure-row) > td:last-child {
        border-right: 0 !important;
    }
`;

const DimensionDropdownPanel = styled.div`
    .ant-checkbox-wrapper-disabled .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
        border-color: #ffffff !important;
        opacity: 1;
    }
`;

const KpiDropdownPanel = styled.div`
    .ant-checkbox-wrapper-disabled .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
        border-color: #ffffff !important;
        opacity: 1;
    }
`;

export const SortUpIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none" aria-hidden>
        <path
            d="M7.94188 4.57036L4.21022 0.0960542C4.1034 -0.0320181 3.89773 -0.0320181 3.78978 0.0960542L0.0581151 4.57036C-0.0805157 4.7372 0.0444792 4.9816 0.268334 4.9816H7.73167C7.95552 4.9816 8.08052 4.7372 7.94188 4.57036Z"
            fill="black"
            fillOpacity="0.25"
        />
    </svg>
);

export const SortDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none" aria-hidden>
        <path
            d="M7.73167 0.0184H0.268334C0.0444792 0.0184 -0.0805157 0.2628 0.0581151 0.42964L3.78978 4.9039C3.89659 5.032 4.10227 5.032 4.21022 4.9039L7.94188 0.42964C8.08052 0.2628 7.95552 0.0184 7.73167 0.0184Z"
            fill="black"
            fillOpacity="0.25"
        />
    </svg>
);

const HeaderSortAscIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.5286 0.195262C3.78894 -0.0650874 4.21106 -0.0650874 4.4714 0.195262L7.80474 3.5286C8.06509 3.78894 8.06509 4.21106 7.80474 4.4714C7.54439 4.73175 7.12228 4.73175 6.86193 4.4714L4 1.60948L1.13807 4.4714C0.877722 4.73175 0.455612 4.73175 0.195262 4.4714C-0.0650874 4.21106 -0.0650874 3.78894 0.195262 3.5286L3.5286 0.195262Z"
            fill="currentColor"
        />
    </svg>
);

const HeaderSortDescIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 7 8 5" fill="none" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.195262 7.5286C0.455612 7.26825 0.877722 7.26825 1.13807 7.5286L4 10.3905L6.86193 7.5286C7.12228 7.26825 7.54439 7.26825 7.80474 7.5286C8.06509 7.78895 8.06509 8.21106 7.80474 8.4714L4.4714 11.8047C4.21106 12.0651 3.78894 12.0651 3.5286 11.8047L0.195262 8.4714C-0.0650874 8.21106 -0.0650874 7.78895 0.195262 7.5286Z"
            fill="currentColor"
        />
    </svg>
);

const HeaderSortChevrons = ({
    sortActive,
    onSortAsc,
    onSortDesc,
    sortAscTestId,
    sortDescTestId,
}: {
    sortActive?: 'asc' | 'desc' | null;
    onSortAsc: () => void;
    onSortDesc: () => void;
    sortAscTestId?: string;
    sortDescTestId?: string;
}) => (
    <span className="inline-flex shrink-0 flex-col items-center justify-center gap-0.5">
        <button
            type="button"
            className={`inline-flex h-[5px] items-center justify-center transition-colors ${
                sortActive === 'asc' ? 'text-[#44338a]' : 'text-black/30 hover:text-black/55'
            }`}
            onClick={(event) => {
                event.stopPropagation();
                onSortAsc();
            }}
            aria-label="Sort ascending"
            data-testid={sortAscTestId}
        >
            <HeaderSortAscIcon />
        </button>
        <button
            type="button"
            className={`inline-flex h-[5px] items-center justify-center transition-colors ${
                sortActive === 'desc' ? 'text-[#44338a]' : 'text-black/30 hover:text-black/55'
            }`}
            onClick={(event) => {
                event.stopPropagation();
                onSortDesc();
            }}
            aria-label="Sort descending"
            data-testid={sortDescTestId}
        >
            <HeaderSortDescIcon />
        </button>
    </span>
);

const HeaderTitle = ({
    title,
    withVs = false,
    comparisonLabel,
    sortActive,
    onSortAsc,
    onSortDesc,
    pending = false,
    sortAscTestId,
    sortDescTestId,
}: HeaderTitleProps) => (
    <div className={`${tableHeaderContentClassName} flex w-full min-w-0 items-center justify-between gap-2`}>
        <span className="inline-flex min-w-0 flex-wrap justify-center items-center gap-x-1 gap-y-[1px]">
            {pending ? <Spin size="small" className="shrink-0" /> : null}
            <span title={title} className="truncate font-semibold text-base leading-[20px] text-black/88">{title}</span>
            {(comparisonLabel || withVs) && (
                <span className="w-full text-center text-sm font-normal leading-[14px] text-black/45">
                    {`vs ${comparisonLabel || 'YA'}`}
                </span>
            )}
        </span>
        {onSortAsc && onSortDesc ? (
            <HeaderSortChevrons
                sortActive={sortActive ?? null}
                onSortAsc={onSortAsc}
                onSortDesc={onSortDesc}
                sortAscTestId={sortAscTestId}
                sortDescTestId={sortDescTestId}
            />
        ) : null}
    </div>
);

const filterButtonClassName =
    '!gap-2 !inline-flex !h-8 !w-auto !items-center !gap-1 !rounded-[6px] !border-[#d9d9d9] !px-2 !text-base !font-bold !leading-[22px] !text-[rgba(0,0,0,0.88)] !shadow-[0px_2px_0px_rgba(0,0,0,0.02)]';
const filterButtonIconClassName = 'text-[10px] leading-none';
const recordSummaryClassName =
    '!h-9 !rounded-[3px] !px-[16px] !py-[8px] !text-sm !font-normal !leading-normal !text-[#52525C] !bg-[#F0F0F0]';
const dimensionSelectionLimit = 2;



const getPrimaryInputConfig = (inputList?: MetaInputConfig | MetaInputConfig[]) => {
    if (!inputList) return undefined;
    return Array.isArray(inputList) ? inputList[0] : inputList;
};

const getDefaultInputValue = (inputConfig?: MetaInputConfig) =>
    inputConfig?.default_value === undefined || inputConfig.default_value === null ? '' : String(inputConfig.default_value);

const toNumericValue = (rawValue: string) => {
    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const clampToBounds = (value: number, bounds?: MetaInputBounds) => {
    const minValue = bounds?.min_val;
    const maxValue = bounds?.max_val;
    if (typeof minValue === 'number' && value < minValue) return minValue;
    if (typeof maxValue === 'number' && value > maxValue) return maxValue;
    return value;
};

const bumpInputValue = (currentValue: string, delta: number, inputConfig?: MetaInputConfig) => {
    const step = inputConfig?.increment_rate ?? 1;
    const currentNumeric = toNumericValue(currentValue);
    const fallbackBase =
        currentNumeric ??
        (typeof inputConfig?.default_value === 'number' ? inputConfig.default_value : undefined) ??
        inputConfig?.bounds?.min_val ??
        0;
    const nextValue = clampToBounds(fallbackBase + delta * step, inputConfig?.bounds);
    return String(nextValue);
};

const normalizeInputValue = (rawValue: string, inputConfig?: MetaInputConfig) => {
    if (!rawValue.trim()) return '';
    const numericValue = toNumericValue(rawValue);
    if (numericValue === null) return rawValue;
    return String(clampToBounds(numericValue, inputConfig?.bounds));
};

const sanitizeNumericInput = (rawValue: string) => {
    const filtered = rawValue.replace(/[^0-9.-]/g, '');
    if (!filtered) return '';

    const hasLeadingMinus = filtered.startsWith('-');
    const unsignedValue = hasLeadingMinus ? filtered.slice(1) : filtered;
    const withoutExtraMinus = unsignedValue.replace(/-/g, '');
    const [integerPart, ...decimalParts] = withoutExtraMinus.split('.');
    const decimalPart = decimalParts.join('');
    const normalizedNumber = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;

    return `${hasLeadingMinus ? '-' : ''}${normalizedNumber}`;
};

export const InputKpiHeader = ({
    title,
    comparisonLabel,
    value,
    placeholder,
    onChange,
    onBlur,
    onStepChange,
    sortActive,
    onSortAsc,
    onSortDesc,
    pending = false,
    inputTestId,
    stepUpTestId,
    stepDownTestId,
    sortAscTestId,
    sortDescTestId,
}: {
    title: string;
    comparisonLabel?: string;
    value: string;
    placeholder?: string;
    onChange: (nextValue: string) => void;
    onBlur: () => void;
    onStepChange: (delta: 1 | -1) => void;
    sortActive?: 'asc' | 'desc' | null;
    onSortAsc?: () => void;
    onSortDesc?: () => void;
    pending?: boolean;
    inputTestId?: string;
    stepUpTestId?: string;
    stepDownTestId?: string;
    sortAscTestId?: string;
    sortDescTestId?: string;
}) => (
    <div className={`${tableHeaderContentClassName} flex w-full min-w-0 items-center justify-between gap-1`}>
        <span title={`${title.trim()}${comparisonLabel ? ` vs ${comparisonLabel}` : ''}`} className="inline-flex min-w-0 shrink flex-wrap justify-center items-center gap-x-1 gap-y-[1px]">
            {pending ? <Spin size="small" className="shrink-0" /> : null}
            <span className="min-w-0 truncate font-semibold text-base leading-[20px] text-black/88">
                {title.trim()}
            </span>
            {comparisonLabel ? (
                <span className="w-full text-center text-sm font-normal leading-[14px] text-black/45">
                    {`vs ${comparisonLabel}`}
                </span>
            ) : null}
        </span>
        <div className="flex h-[25px] shrink-0 items-center justify-center rounded-[4px] border border-[#d9d9d9] bg-white px-[6px]">
            <div className="mr-1 inline-flex h-[18px] items-center justify-start text-base leading-[18px] text-[#52525C]">
                <span>±</span>
            </div>
            <input
                value={value}
                inputMode="decimal"
                onChange={(event) => onChange(sanitizeNumericInput(event.target.value))}
                onKeyDown={(event) => {
                    if (event.ctrlKey || event.metaKey || event.altKey) return;

                    const allowedKeys = new Set([
                        'Backspace',
                        'Delete',
                        'Tab',
                        'Enter',
                        'Escape',
                        'ArrowLeft',
                        'ArrowRight',
                        'ArrowUp',
                        'ArrowDown',
                        'Home',
                        'End',
                    ]);
                    if (allowedKeys.has(event.key)) return;

                    if (event.key === '-') {
                        const cursorPosition = event.currentTarget.selectionStart ?? 0;
                        if (cursorPosition !== 0 || event.currentTarget.value.includes('-')) event.preventDefault();
                        return;
                    }

                    if (event.key === '.') {
                        if (event.currentTarget.value.includes('.')) event.preventDefault();
                        return;
                    }

                    if (!/^\d$/.test(event.key)) event.preventDefault();
                }}
                onBlur={onBlur}
                aria-label={`${title} input`}
                data-testid={inputTestId}
                className="h-[18px] w-[18px] border-0 bg-transparent p-0 text-base leading-[18px] text-[#52525C] placeholder:text-sm placeholder:text-[#52525C] focus:outline-none"
            />
            <div className="inline-flex h-[18px] items-center select-none text-xs leading-[18px] text-[#52525C]">%</div>
            <div className="ml-1 inline-flex h-[14px] w-2 flex-col justify-between text-[8px] leading-[6px]">
                <button
                    type="button"
                    className="inline-flex h-[5px] items-center justify-center text-black/30 transition-colors hover:text-black/55"
                    onClick={() => onStepChange(1)}
                    aria-label={`Increase ${title}`}
                    data-testid={stepUpTestId}
                >
                    <SortUpIcon />
                </button>
                <button
                    type="button"
                    className="inline-flex h-[5px] items-center justify-center text-black/30 transition-colors hover:text-black/55"
                    onClick={() => onStepChange(-1)}
                    aria-label={`Decrease ${title}`}
                    data-testid={stepDownTestId}
                >
                    <SortDownIcon />
                </button>
            </div>
        </div>
        {onSortAsc && onSortDesc ? (
            <HeaderSortChevrons
                sortActive={sortActive ?? null}
                onSortAsc={onSortAsc}
                onSortDesc={onSortDesc}
                sortAscTestId={sortAscTestId}
                sortDescTestId={sortDescTestId}
            />
        ) : null}
    </div>
);

/** Table body scroll lives on InfiniteScroller parent; avoids nested vertical scrollbars. */
const StyledInfiniteTableWrapper = styled(StyledTableWrapper)`
    .ant-table-header {
        position: sticky !important;
        top: 0 !important;
        z-index: 22 !important;
    }

    .ant-table-thead {
        position: sticky !important;
        top: 0 !important;
        z-index: 20 !important;
    }

    .ant-table-thead > tr > th {
        position: sticky !important;
        top: 0 !important;
        z-index: 21 !important;
        background: #fafafa !important;
    }

    .ant-table-content {
        overflow-x: auto !important;
        overflow-y: auto !important;
    }

    .ant-table-body {
        overflow: auto !important;
        flex: initial !important;
        max-height: none !important;
    }
`;

export {
    DimensionDropdownPanel,
    HeaderTitle,
    KpiDropdownPanel,
    SectionCard,
    StyledSectionTabs,
    StyledTableWrapper,
    StyledInfiniteTableWrapper,
    dimensionSelectionLimit,
    filterButtonClassName,
    filterButtonIconClassName,
    recordSummaryClassName,
    selectedRowClassName,
    tableHeaderContentClassName,
    getPrimaryInputConfig,
    getDefaultInputValue,
    normalizeInputValue,
    bumpInputValue
};
